import type { FilterConfig } from '@/types/filters'
import { LOTE_1_FILTER_CONFIGS } from './filterConfigs'
import { LOTE_2_FILTER_CONFIGS, LOTE_2_FIELD_ACCESSORS } from './filterConfigsLote2'
import { LOTE_3_FILTER_CONFIGS, LOTE_3_FIELD_ACCESSORS } from './filterConfigsLote3'
import { LOTE_4_FILTER_CONFIGS, LOTE_4_FIELD_ACCESSORS } from './filterConfigsLote4'
import { LOTE_5_FILTER_CONFIGS, LOTE_5_FIELD_ACCESSORS } from './filterConfigsLote5'
import { LOTE_6_FILTER_CONFIGS, LOTE_6_FIELD_ACCESSORS } from './filterConfigsLote6'
import { LOTE_7_FILTER_CONFIGS, LOTE_7_FIELD_ACCESSORS } from './filterConfigsLote7'
import { LOTE_8_FILTER_CONFIGS, LOTE_8_FIELD_ACCESSORS } from './filterConfigsLote8'
import { ALLOCATION_FILTER_CONFIGS, ALLOCATION_FIELD_ACCESSORS } from './filterConfigsAllocation'
import { DISPLAY_COLUMN_FILTER_CONFIGS, DISPLAY_COLUMN_FIELD_ACCESSORS } from './filterConfigsDisplayColumns'

/**
 * Configurações combinadas: TODOS os lotes implementados - SISTEMA COMPLETO! 
 * 
 * LOTE 1 (7 campos): Nome, Status, Departamento, Cargo, Cidade, Estado, Email
 * LOTE 2 (15 campos): Dados Pessoais + Dados Contratuais
 * LOTE 3 (8 campos): Dados Profissionais + Responsáveis
 * LOTE 4 (12 campos): Benefícios + Remuneração
 * LOTE 5 (8 campos): Dados PJ + Dados Acadêmicos
 * LOTE 6 (8 campos): Performance + Engajamento
 * LOTE 7 (15 campos): Localização + Logística
 * LOTE 8 (15 campos): Auditoria + Campos Customizados
 * 
 * TOTAL FINAL: todos os campos exibíveis da tabela cobertos nos filtros avançados.
 */

export const ALL_FILTER_CONFIGS: FilterConfig[] = [
  ...LOTE_1_FILTER_CONFIGS,
  ...LOTE_2_FILTER_CONFIGS,
  ...LOTE_3_FILTER_CONFIGS,
  ...LOTE_4_FILTER_CONFIGS,
  ...LOTE_5_FILTER_CONFIGS,
  ...LOTE_6_FILTER_CONFIGS,
  ...LOTE_7_FILTER_CONFIGS,
  ...LOTE_8_FILTER_CONFIGS,
  ...DISPLAY_COLUMN_FILTER_CONFIGS,
  ...ALLOCATION_FILTER_CONFIGS
]

/**
 * Field Accessors combinados de todos os lotes - SISTEMA COMPLETO!
 */
export const ALL_FIELD_ACCESSORS = {
  // Lote 1 - Campos básicos
  departmentName: (item: any) => item.departmentName,
  positionName: (item: any) => item.positionName,
  addressCity: (item: any) => item.addresses?.[0]?.city,
  addressState: (item: any) => item.addresses?.[0]?.state,
  
  // Lote 2 - Dados pessoais e contratuais
  ...LOTE_2_FIELD_ACCESSORS,
  
  // Lote 3 - Dados profissionais e responsáveis
  ...LOTE_3_FIELD_ACCESSORS,
  
  // Lote 4 - Benefícios e remuneração
  ...LOTE_4_FIELD_ACCESSORS,
  
  // Lote 5 - Dados PJ e acadêmicos
  ...LOTE_5_FIELD_ACCESSORS,
  
  // Lote 6 - Performance e engajamento
  ...LOTE_6_FIELD_ACCESSORS,
  
  // Lote 7 - Localização e logística
  ...LOTE_7_FIELD_ACCESSORS,
  
  // Lote 8 - Auditoria e campos customizados
  ...LOTE_8_FIELD_ACCESSORS,

  // Campos exibíveis da tabela que não pertencem aos lotes originais
  ...DISPLAY_COLUMN_FIELD_ACCESSORS,

  // Alocação
  ...ALLOCATION_FIELD_ACCESSORS,
}

/**
 * Campos que precisam ser populados dinamicamente
 */
export const DYNAMIC_FILTER_FIELDS = [
  'departmentName',
  'positionName', 
  'addressCity',
  'nationality',
  'workSchedule',
  'seniorityName',
  'responsibleName',
  'healthInsuranceProvider',
  'educationalInstitution',
  'course',
  'internshipSupervisor',
  'neighborhood',
  'primaryWorkLocation',
  'createdBy',
  'lastUpdatedBy',
  'tags',
  'allocationClientNames',
  'allocationSummary',
  'internshipEducationalInstitution',
  'internshipCourse',
  'lastSalaryReason',
  'mainEducationInstitution',
  'primarySkill',
] as const

/**
 * Re-exporta função utilitária
 */
export { updateFilterConfigsWithData } from './filterConfigs'

/**
 * Estatísticas finais do sistema completo de filtros.
 */
export const FILTER_STATS = {
  totalFields: ALL_FILTER_CONFIGS.length,
  byCategory: {
    'Dados Pessoais': ALL_FILTER_CONFIGS.filter(f => f.category === 'Dados Pessoais').length,
    'Dados Profissionais': ALL_FILTER_CONFIGS.filter(f => f.category === 'Dados Profissionais').length,
    'Dados Contratuais': ALL_FILTER_CONFIGS.filter(f => f.category === 'Dados Contratuais').length,
    'Dados de Contato': ALL_FILTER_CONFIGS.filter(f => f.category === 'Dados de Contato').length,
    'Localização': ALL_FILTER_CONFIGS.filter(f => f.category === 'Localização').length,
    'Status': ALL_FILTER_CONFIGS.filter(f => f.category === 'Status').length,
    'Benefícios': ALL_FILTER_CONFIGS.filter(f => f.category === 'Benefícios').length,
    'Remuneração': ALL_FILTER_CONFIGS.filter(f => f.category === 'Remuneração').length,
    'Dados PJ': ALL_FILTER_CONFIGS.filter(f => f.category === 'Dados PJ').length,
    'Dados Acadêmicos': ALL_FILTER_CONFIGS.filter(f => f.category === 'Dados Acadêmicos').length,
    'Performance': ALL_FILTER_CONFIGS.filter(f => f.category === 'Performance').length,
    'Engajamento': ALL_FILTER_CONFIGS.filter(f => f.category === 'Engajamento').length,
    'Logística': ALL_FILTER_CONFIGS.filter(f => f.category === 'Logística').length,
    'Auditoria': ALL_FILTER_CONFIGS.filter(f => f.category === 'Auditoria').length,
    'Campos Customizados': ALL_FILTER_CONFIGS.filter(f => f.category === 'Campos Customizados').length,
    'Recursos Técnicos': ALL_FILTER_CONFIGS.filter(f => f.category === 'Recursos Técnicos').length,
    'Alocação': ALL_FILTER_CONFIGS.filter(f => f.category === 'Alocação').length,
    'Dados Específicos': ALL_FILTER_CONFIGS.filter(f => f.category === 'Dados Específicos').length,
    'Dados de Estagiário': ALL_FILTER_CONFIGS.filter(f => f.category === 'Dados de Estagiário').length,
    'Informações de Saúde': ALL_FILTER_CONFIGS.filter(f => f.category === 'Informações de Saúde').length,
    'Preferências Pessoais': ALL_FILTER_CONFIGS.filter(f => f.category === 'Preferências Pessoais').length,
    'Dados Contratuais Finais': ALL_FILTER_CONFIGS.filter(f => f.category === 'Dados Contratuais Finais').length,
    'Contadores': ALL_FILTER_CONFIGS.filter(f => f.category === 'Contadores').length,
    'Endereços': ALL_FILTER_CONFIGS.filter(f => f.category === 'Endereços').length,
    'Métricas Salariais': ALL_FILTER_CONFIGS.filter(f => f.category === 'Métricas Salariais').length,
    'Gestão de Férias': ALL_FILTER_CONFIGS.filter(f => f.category === 'Gestão de Férias').length,
    'Formação Acadêmica': ALL_FILTER_CONFIGS.filter(f => f.category === 'Formação Acadêmica').length,
  },
  byType: {
    text: ALL_FILTER_CONFIGS.filter(f => f.type === 'text').length,
    select: ALL_FILTER_CONFIGS.filter(f => f.type === 'select').length,
    multiselect: ALL_FILTER_CONFIGS.filter(f => f.type === 'multiselect').length,
    date: ALL_FILTER_CONFIGS.filter(f => f.type === 'date').length,
    number: ALL_FILTER_CONFIGS.filter(f => f.type === 'number').length,
    boolean: ALL_FILTER_CONFIGS.filter(f => f.type === 'boolean').length,
  }
} as const

/**
 * Presets combinados (exemplo de como seria)
 */
export const COMBINED_PRESETS = [
  {
    id: 'colaboradores_completo',
    name: 'Perfil Completo de Colaboradores',
    description: 'Colaboradores CLT ativos com dados pessoais e profissionais completos',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'collaboratorType',
        operator: 'equals' as const,
        value: 'CLT',
        type: 'select' as const,
        label: 'Tipo de Colaborador',
        enabled: true
      },
      {
        columnId: 'phone',
        operator: 'isNotEmpty' as const,
        value: null,
        type: 'text' as const,
        label: 'Telefone',
        enabled: true
      },
      {
        columnId: 'responsibleName',
        operator: 'isNotEmpty' as const,
        value: null,
        type: 'text' as const,
        label: 'Gestor Responsável',
        enabled: true
      }
    ],
    isSystem: true
  }
] as const
